import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query';
import {
  apiGet,
  apiGetList,
  apiGetById,
  apiGetRaw,
  apiPost,
  apiPut,
  apiDelete,
  apiUpload,
} from '../services/apiClient';

/**
 * Navigation menus for the public website — items, placements and the display
 * mode.
 *
 * Every call goes through `services/apiClient`, which applies the Backend API
 * Contract in one place: the envelope assertion, the `error`-over-`message`
 * precedence and the `{ items, totalCount, currentPage, pageSize }` list shape.
 * Do NOT copy `Hooks/HomeSlider.js`, which predates that rule and calls axios
 * directly.
 *
 * Backend routes, probed live 2026-09-22:
 *
 *   GET    /api/NavigationMenu/Menu/{placementType}                 ANONYMOUS - the public read model
 *   GET    /api/NavigationMenu/Items/{page}/{pageSize}
 *   GET    /api/NavigationMenu/Items/{id}
 *   POST   /api/NavigationMenu/Items
 *   PUT    /api/NavigationMenu/Items/{id}
 *   DELETE /api/NavigationMenu/Items/{id}
 *   GET    /api/NavigationMenu/Placements/{placementType}/{page}/{pageSize}
 *   POST   /api/NavigationMenu/Placements
 *   PUT    /api/NavigationMenu/Placements/{id}
 *   DELETE /api/NavigationMenu/Placements/{id}
 *   PUT    /api/NavigationMenu/Placements/Reorder
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ PAGE IS ZERO ON THIS CONTROLLER'S LIST URLS.                             │
 * │                                                                          │
 * │   History, because it has flip-flopped and the failure mode is silent:    │
 * │                                                                          │
 * │   - Measured 2026-09-22: this controller (and `Pages`) shipped 0-BASED    │
 * │     while ContactUs and the rest were 1-based (`GET /api/Pages/0/1`       │
 * │     returned the first row). Raised as ask F; the backend team said the   │
 * │     whole API would standardise on ONE-based, and this file switched to   │
 * │     page 1 on that promise.                                              │
 * │   - 2026-09-23: the backend team's own documented list URLs for this      │
 * │     controller are `/Placements/{type}/0/0` - PAGE ZERO. The ask-F        │
 * │     standardisation did not land here. `FIRST_PAGE` is 0 again so the     │
 * │     requests match the URLs the backend publishes, verbatim.             │
 * │                                                                          │
 * │   Every read in this file uses `pageSize: 0` ("return all"), so the page  │
 * │   index selects nothing either way - but on a 0-based controller a page   │
 * │   of 1 has previously meant "silently skip the first rows", and that is   │
 * │   not a risk worth carrying for the sake of a prettier number.           │
 * │                                                                          │
 * │   ContactUs remains 1-BASED (measured: `/0/2` is a 500 there). The page   │
 * │   base is PER-CONTROLLER on this API - see DPA-CONFORMANCE.md.           │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * `pageSize: 0` means "return all".
 */

/** Matches NavigationPlacementType on the server. */
export const PLACEMENT_TYPE = Object.freeze({
  MAIN_MENU: 1,
  ACTION_BUTTON: 2,
  FEATURED_MENU: 3,
  BOTTOM_MENU: 4,
  SEARCH_MENU: 5,
});

/**
 * Matches NavigationLinkType on the server — an enum that has been GROWING:
 *
 *   2026-09-22 (feature built):   [Internal, External]            = 0, 1
 *   2026-09-25 (Swagger, live):   [Internal, External, Section]   = 0, 1, 2
 *   2026-09-25 (agreed, pending): Dynamic                          = 3
 *   2026-09-29 (probed, live):    Action                           = 4
 *
 * ACTION is the member the deployed API accepts for a destination that names
 * NO page: an `action:` key or a `#section-n` scroll. Sending 0 (Internal) for
 * those is rejected — `InternalPageId is required when LinkType is not
 * External & DynamicUrl` — because 0 promises a page id the destination has
 * no way to supply. 2 (Section) is NOT that exemption: it fails the same
 * validation. Confirmed against the live API 2026-09-29.
 *
 * SECTION (2) is what «قسم من الصفحة الرئيسية» sends as of 2026-09-30, and it
 * sends an `internalPageId` WITH it. That is what makes 2 legal where the
 * probe above found it rejected: the probe used a `#section-n` scroll, which
 * names no page and therefore has no id to give, so that destination still
 * uses ACTION (4). A catalogued page section names a real row of the Pages
 * catalogue, so it satisfies `InternalPageId is required…` honestly.
 * `MenuItemEditor` resolves the id from the catalogue by slug — including for
 * rows saved before this, which carry a slug and no id.
 *
 * DYNAMIC is the agreement with the backend team for the «صفحة ديناميكية»
 * destination: the item request carries `linkType: 3` plus the URL in a new
 * `dynamicUrl` field. Neither the enum member nor `dynamicUrl` is in Swagger
 * yet; until the backend recognises 3, saving a dynamic item may be rejected
 * by server-side validation — a correct, visible outcome per the house rule.
 * When the deployed enum next changes, re-probe before assuming these hold.
 */
export const LINK_TYPE = Object.freeze({
  INTERNAL: 0,
  EXTERNAL: 1,
  SECTION: 2,
  DYNAMIC: 3,
  ACTION: 4,
});

/**
 * The five placements, in the order the settings screen shows them, with the
 * region of the website each one drives.
 *
 * The enum is declared in Swagger as a bare integer with no names, so these
 * Arabic labels are owned by the client. Mapping approved 2026-09-22 — see
 * decision D1 in DPA-FE `docs/Controlled/MainMenu/README.md`.
 */
export const PLACEMENTS = Object.freeze([
  {
    type: PLACEMENT_TYPE.MAIN_MENU,
    key: 'MainMenu',
    label: 'القائمة الرئيسية',
    hint: 'القائمة الكاملة التي تظهر عند الضغط على زر القائمة في أعلى الموقع',
    supportsNesting: true,
    supportsButtons: false,
  },
  {
    type: PLACEMENT_TYPE.ACTION_BUTTON,
    key: 'ActionButton',
    label: 'أزرار الإجراءات',
    hint: 'أزرار أعلى الموقع: البحث، أكاديمية 360، إمكانية الوصول، اللغة، تسجيل الدخول',
    supportsNesting: false,
    supportsButtons: true,
    // RSP-03 balanced this row to 259px inside a 280px content box at 320px.
    // Adding a sixth control overflows the smallest supported viewport, so the
    // screen caps it until a responsive QA pass says otherwise.
    maxItems: 5,
  },
  {
    type: PLACEMENT_TYPE.FEATURED_MENU,
    key: 'FeaturedMenu',
    label: 'القوائم المميزة',
    hint: 'الروابط السريعة بجانب زر القائمة في أعلى الموقع',
    supportsNesting: false,
    supportsButtons: false,
  },
  {
    type: PLACEMENT_TYPE.BOTTOM_MENU,
    key: 'BottomMenu',
    label: 'القائمة السفلية',
    hint: 'الشريط السفلي في الصفحة الرئيسية، وزر التقديم',
    supportsNesting: false,
    supportsButtons: true,
  },
  {
    type: PLACEMENT_TYPE.SEARCH_MENU,
    key: 'SearchMenu',
    label: 'قائمة البحث',
    hint: 'قائمة الاقتراحات في نافذة البحث. إذا تُركت فارغة يعرض الموقع القائمة الرئيسية.',
    supportsNesting: true,
    supportsButtons: false,
    optional: true,
  },
]);

export const placementByType = (type) =>
  PLACEMENTS.find((placement) => placement.type === Number(type)) ?? null;

/** How the website should build its menus. Mirrors the requested backend enum. */
export const MENU_MODE = Object.freeze({
  LEGACY: 0, // كما هو حالياً — the website renders its own built-in menus
  CONTROLLED: 1, // التحكم بالقوائم — the website renders this configuration
});

const RESOURCE = 'NavigationMenu';

/** ZERO on this controller — see the banner above; the backend's documented
 *  list URLs are `/{type}/0/0`, re-confirmed 2026-09-23. */
const FIRST_PAGE = 0;
/** `pageSize: 0` means "return every row". */
const ALL = 0;

export const navigationKeys = {
  all: ['navigation'],
  items: () => ['navigation', 'items'],
  item: (id) => ['navigation', 'items', id],
  placements: (type) => ['navigation', 'placements', Number(type)],
  menu: (type) => ['navigation', 'menu', Number(type)],
  settings: () => ['navigation', 'settings'],
  pages: (type) => ['navigation', 'pages', Number(type)],
  degrees: () => ['navigation', 'degrees'],
};

/* -------------------------------------------------------------------------- */
/*  Items                                                                      */
/* -------------------------------------------------------------------------- */

export const useNavigationItems = ({ page = FIRST_PAGE, pageSize = ALL } = {}) =>
  useQuery({
    queryKey: [...navigationKeys.items(), page, pageSize],
    queryFn: () => apiGetList(`${RESOURCE}/Items`, { page, pageSize }),
    refetchOnWindowFocus: false,
  });

export const useNavigationItem = (id, { enabled = true } = {}) =>
  useQuery({
    queryKey: navigationKeys.item(id),
    queryFn: () => apiGetById(`${RESOURCE}/Items`, id),
    enabled: Boolean(id) && enabled,
    refetchOnWindowFocus: false,
  });

/**
 * @param {object} payload NavigationItemRequest
 *   { title, description, linkType, internalPageId, externalUrl, iconUrl,
 *     openInNewTab, isActive, isDisplayTypeButton, isShowButtonText }
 */
export const useCreateNavigationItem = () => {
  const queryClient = useQueryClient();
  return useMutation((payload) => apiPost(`${RESOURCE}/Items`, payload), {
    onSuccess: () => queryClient.invalidateQueries(navigationKeys.all),
  });
};

export const useUpdateNavigationItem = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, ...payload }) => apiPut(`${RESOURCE}/Items/${id}`, payload),
    { onSuccess: () => queryClient.invalidateQueries(navigationKeys.all) }
  );
};

export const useDeleteNavigationItem = () => {
  const queryClient = useQueryClient();
  return useMutation((id) => apiDelete(`${RESOURCE}/Items`, id), {
    onSuccess: () => queryClient.invalidateQueries(navigationKeys.all),
  });
};

/* -------------------------------------------------------------------------- */
/*  Placements                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Placements of one type, as a flat list straight from the API.
 * Use `buildPlacementTree` to nest them for display.
 */
export const usePlacements = (placementType, { enabled = true } = {}) =>
  useQuery({
    queryKey: navigationKeys.placements(placementType),
    queryFn: () =>
      apiGetList(`${RESOURCE}/Placements/${placementType}`, {
        page: FIRST_PAGE,
        pageSize: ALL,
      }),
    enabled: Boolean(placementType) && enabled,
    refetchOnWindowFocus: false,
  });

/**
 * @param {object} payload NavigationItemPlacementRequest
 *   { navigationItemId, placementType, parentPlacementId, linkOverride,
 *     isVisible, sequenceId }
 */
export const useCreatePlacement = () => {
  const queryClient = useQueryClient();
  return useMutation((payload) => apiPost(`${RESOURCE}/Placements`, payload), {
    onSuccess: () => queryClient.invalidateQueries(navigationKeys.all),
  });
};

export const useUpdatePlacement = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, ...payload }) => apiPut(`${RESOURCE}/Placements/${id}`, payload),
    { onSuccess: () => queryClient.invalidateQueries(navigationKeys.all) }
  );
};

export const useDeletePlacement = () => {
  const queryClient = useQueryClient();
  return useMutation((id) => apiDelete(`${RESOURCE}/Placements`, id), {
    onSuccess: () => queryClient.invalidateQueries(navigationKeys.all),
  });
};

/**
 * Reorder a placement type.
 *
 * ⚠ `ReorderRequest` carries NO `parentPlacementId`, so for the 3-level main
 * menu it is ambiguous whether this reorders one sibling group or flattens the
 * whole type. Raised as ask E.1 in
 * DPA-FE `docs/Controlled/MainMenu/09-backend-request.md`.
 *
 * Until the backend confirms, the screen reorders via `useReorderSiblings`
 * below, which writes `sequenceId` per row and is unambiguous. This hook is
 * kept ready for the day the semantics are settled.
 */
export const useReorderPlacements = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ placementType, orderedPlacementIds }) =>
      apiPut(`${RESOURCE}/Placements/Reorder`, {
        placementType,
        orderedPlacementIds,
      }),
    { onSuccess: () => queryClient.invalidateQueries(navigationKeys.all) }
  );
};

/**
 * Reorder one sibling group by writing `sequenceId` on each row.
 *
 * Chatty but unambiguous, and it cannot reorder a group it was not given.
 * Writes run in sequence rather than in parallel so a partial failure leaves a
 * prefix of the intended order rather than an arbitrary interleaving.
 */
export const useReorderSiblings = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ placements }) => {
      const results = [];
      for (let index = 0; index < placements.length; index += 1) {
        const placement = placements[index];
        if (placement.sequenceId === index) continue;
        results.push(
          // eslint-disable-next-line no-await-in-loop
          await apiPut(`${RESOURCE}/Placements/${placement.id}`, {
            navigationItemId: placement.navigationItemId,
            placementType: placement.placementType,
            parentPlacementId: placement.parentPlacementId ?? null,
            linkOverride: placement.linkOverride ?? null,
            isVisible: placement.isVisible,
            sequenceId: index,
          })
        );
      }
      return results;
    },
    { onSuccess: () => queryClient.invalidateQueries(navigationKeys.all) }
  );
};

/**
 * Where one navigation item already appears, across ALL five placements.
 *
 * The API models item and placement separately precisely so one item can sit in
 * several menus, but nothing in the UI made that reachable: an operator who
 * wanted «الإحصائيات» in both the main menu and the featured row had to author it
 * twice, producing two items that then drift apart. This powers the «نسخ إلى
 * قوائم أخرى» control in the item editor, which writes a second PLACEMENT for
 * the SAME `navigationItemId` — one item, many menus.
 *
 * Reads every placement type through the SAME query key and query function as
 * `usePlacements`, so the type already on screen is served from cache and a
 * mutation invalidating `navigation` refreshes this map too.
 *
 * Per type it reports:
 *   - `hasItem`    — the item is already placed here, so offering «نسخ» would
 *                    create a duplicate row in one menu;
 *   - `rootCount`  — top-level rows, which is what `maxItems` caps (the action
 *                    bar is capped at 5 by RSP-03) and what the next
 *                    `sequenceId` has to continue from;
 *   - `isError`    — a type whose list failed to load is NOT reported as empty.
 *                    Treating an unreadable menu as «the item is not here» is how
 *                    a duplicate gets written.
 *
 * @param {string|null} navigationItemId  null while creating — a brand-new item
 *   is nowhere yet, so every type comes back `hasItem: false`.
 */
export const useItemPlacements = (navigationItemId, { enabled = true } = {}) => {
  const results = useQueries(
    PLACEMENTS.map((config) => ({
      queryKey: navigationKeys.placements(config.type),
      queryFn: () =>
        apiGetList(`${RESOURCE}/Placements/${config.type}`, {
          page: FIRST_PAGE,
          pageSize: ALL,
        }),
      enabled,
      refetchOnWindowFocus: false,
    }))
  );

  const wanted = navigationItemId == null ? null : String(navigationItemId);

  const byType = PLACEMENTS.map((config, index) => {
    const result = results[index] ?? {};
    const rows = Array.isArray(result.data?.items) ? result.data.items : [];
    const roots = rows.filter((row) => !row.parentPlacementId);
    const occurrences = wanted
      ? rows.filter((row) => String(row.navigationItemId) === wanted)
      : [];

    return {
      type: config.type,
      config,
      isLoading: Boolean(result.isLoading),
      isError: Boolean(result.isError),
      error: result.error ?? null,
      hasItem: occurrences.length > 0,
      occurrences: occurrences.length,
      rootCount: roots.length,
      // Children included — this is what the settings screen shows on each
      // menu tab, and a nested main-menu row is still a row.
      totalRows: rows.length,
      // A copy always lands at the end of the destination's TOP level.
      nextSequenceId: roots.length,
    };
  });

  return {
    byType,
    isLoading: byType.some((entry) => entry.isLoading),
    isError: byType.some((entry) => entry.isError),
  };
};

/* -------------------------------------------------------------------------- */
/*  Display mode                                                               */
/* -------------------------------------------------------------------------- */

/**
 * ⚠ NOT DEPLOYED YET. `GET/PUT /api/NavigationMenu/Settings` is ask A in
 * DPA-FE `docs/Controlled/MainMenu/09-backend-request.md` — the one item that
 * blocks the feature.
 *
 * The call is written to the contract anyway, per the house rule: a conforming
 * request that 404s is the correct, visible outcome, and the network tab then
 * shows unambiguously that the server is the missing half. `isEndpointMissing`
 * lets the screen say exactly that instead of "please try again".
 */
export const useMenuMode = () => {
  const query = useQuery({
    queryKey: navigationKeys.settings(),
    queryFn: () => apiGet(`${RESOURCE}/Settings`),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const status = query.error?.status ?? null;

  return {
    ...query,
    mode: query.data?.menuMode ?? MENU_MODE.LEGACY,
    updatedOn: query.data?.updatedOn ?? null,
    updatedBy: query.data?.updatedBy ?? null,
    isEndpointMissing: status === 404 || status === 405,
  };
};

export const useSetMenuMode = () => {
  const queryClient = useQueryClient();
  return useMutation((menuMode) => apiPut(`${RESOURCE}/Settings`, { menuMode }), {
    onSuccess: () => queryClient.invalidateQueries(navigationKeys.settings()),
  });
};

/* -------------------------------------------------------------------------- */
/*  Internal page catalogue                                                    */
/* -------------------------------------------------------------------------- */

/**
 * What a row in the `Pages` catalogue IS. Mirrors the server's `PageType`
 * enum, read from Swagger 2026-09-25:
 *
 *   PageType { Page = 0, Section = 1, Modal = 2 }
 *
 * `Page`    — a route the website serves, e.g. `/about/history`.
 * `Section` — a region WITHIN a page that a link can jump to, e.g. `/#news`.
 * `Modal`   — catalogued but not offered by the menu editor: a menu entry
 *             that opens a modal is an ACTION (`action:` key, decision D3),
 *             not a destination, and the website resolves it from its own
 *             whitelist rather than from a stored link.
 */
export const PAGE_TYPE = Object.freeze({
  PAGE: 0,
  SECTION: 1,
  MODAL: 2,
});

/**
 * The pages — or page sections — a menu item can link to internally.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ THE TYPE IS A THIRD PATH SEGMENT. THIS HAS ALREADY FLIP-FLOPPED ONCE.    │
 * │                                                                          │
 * │   GET /api/Pages/{page}/{pageSize}/{type}        ✅ 401 — route exists    │
 * │   GET /api/Pages/{page}/{pageSize}?type={type}   ❌ 404 — route removed   │
 * │   GET /api/Pages/{page}/{pageSize}               ❌ 404 — route removed   │
 * │                                                                          │
 * │   Measured live 2026-09-29. On 2026-09-25 the EXACT OPPOSITE held: the    │
 * │   two-segment route answered 401 and carried `type` as a query parameter  │
 * │   (Swagger declared it `in: query`), and the three-segment form 404'd.    │
 * │   The backend has since replaced one with the other.                      │
 * │                                                                          │
 * │   ⚠ Note this is a DEVIATION from the signed contract, which puts         │
 * │   pagination in the path and filters in the QUERY STRING (§5.1). The      │
 * │   backend is the authority on its own routes, so the client follows —     │
 * │   but 401-vs-404 is the only way to tell these apart from outside, and    │
 * │   the failure is silent in the UI (an empty dropdown). RE-PROBE BOTH      │
 * │   FORMS before assuming either still holds.                               │
 * │                                                                          │
 * │   `apiGetList`'s `segments` option appends the trailing path part; see    │
 * │   the note there, and DPA-CONFORMANCE.md.                                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠ The `Page` catalogue holds 11 of the website's ~37 linkable routes today,
 * and is read-only — ask B in the backend request. Until it is complete, most
 * items carry their path in `placement.linkOverride` instead, which is why the
 * item editor still offers a path field beside this dropdown.
 *
 * @param {number} type one of `PAGE_TYPE`; defaults to ordinary pages.
 */
export const usePages = (type = PAGE_TYPE.PAGE) =>
  useQuery({
    queryKey: navigationKeys.pages(type),
    queryFn: () =>
      // -> GET /api/Pages/0/0/{type}
      apiGetList('Pages', {
        page: FIRST_PAGE,
        pageSize: ALL,
        segments: [type],
      }),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

/* -------------------------------------------------------------------------- */
/*  Degree programmes — the live route list                                    */
/* -------------------------------------------------------------------------- */

/**
 * The three programme groups, and the URL word each one contributes.
 *
 * ⚠ THE WEBSITE OWNS THIS MAPPING. The authority is DPA-FE
 * `src/components/organism/site-header/factory/useNavigatorData.jsx`, which
 * builds every live degree link as:
 *
 *   /degree-programs/degree/{bachelors|masters|phds}/{degreeId}
 *
 * keyed off `degreeType` 0/1/2. The CMS mirrors it so an operator can pick a
 * programme instead of pasting a GUID; changing the words here without
 * changing them there produces a link that still resolves (the `:name`
 * segment is decorative in the route `/:type/:name/:id`) but no longer matches
 * what the site's own navigation emits.
 */
export const DEGREE_GROUPS = Object.freeze([
  { degreeType: 0, slug: 'bachelors', label: 'البكالوريوس' },
  { degreeType: 1, slug: 'masters', label: 'الماجستير' },
  { degreeType: 2, slug: 'phds', label: 'الدكتوراه' },
]);

/**
 * ⚠ `pageSize: 0` DOES NOT MEAN "ALL" ON THIS CONTROLLER. Measured
 * 2026-09-25: `GET /api/Degree/GetDegrees/0/1` returns ZERO rows, where the
 * same convention returns everything on `NavigationMenu` and `Pages`. Note
 * also that the segments are reversed here — `{pageSize}/{page}`, the only
 * controller on this API that does that.
 *
 * 17 programmes exist today; 500 is "all of them" with room to grow, and the
 * dropdown reports `totalCount` so a future overflow is visible rather than
 * silently truncated.
 */
const DEGREE_PAGE_SIZE = 500;

/**
 * Every degree programme, for the programme picker in the item editor.
 *
 * Anonymous (measured), and NOT enveloped — hence `apiGetRaw`. See the note on
 * that helper before copying this pattern anywhere else.
 */
export const useDegreeRoutes = () =>
  useQuery({
    queryKey: navigationKeys.degrees(),
    queryFn: () => apiGetRaw(`Degree/GetDegrees/${DEGREE_PAGE_SIZE}/1`),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

/**
 * Shape the degree list into `<optgroup>`-ready groups.
 *
 * A programme whose `degreeType` is none of 0/1/2 is DROPPED rather than
 * guessed into a group: an unknown type would produce a URL word the website
 * does not emit, and a wrong link is worse than a missing one.
 */
export const buildDegreeRouteGroups = (data) => {
  const items = Array.isArray(data?.items) ? data.items : [];

  return DEGREE_GROUPS.map((group) => ({
    ...group,
    options: items
      .filter((item) => item?.degreeType === group.degreeType && item?.degreeId)
      .sort(
        (a, b) =>
          (a.sortIndex ?? 0) - (b.sortIndex ?? 0) ||
          (a.degreeNameAr ?? '').localeCompare(b.degreeNameAr ?? '', 'ar')
      )
      .map((item) => ({
        id: item.degreeId,
        label: item.degreeNameAr || item.degreeNameEn || item.degreeId,
        route: `/degree-programs/degree/${group.slug}/${item.degreeId}`,
      })),
  })).filter((group) => group.options.length > 0);
};

/* -------------------------------------------------------------------------- */
/*  Icon upload                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Upload one icon file and get back a URL to store in `iconUrl`.
 *
 * ⚠ BORROWED ENDPOINT. There is no icon endpoint on this API. Swagger lists
 * exactly two generic uploaders — `POST /api/Degree/UploadFileToBlob` and
 * `POST /api/Alumni/UploadBlob` — both taking a bare `file` and both unused by
 * either project. This uses the first.
 *
 * Two consequences an operator may meet, so the UI must name them rather than
 * say "try again":
 *   - the response shape is UNDOCUMENTED (Swagger says only "Success"), which
 *     is why `extractUploadedUrl` below accepts several shapes and reports a
 *     precise failure when it recognises none;
 *   - whether the blob container accepts `image/svg+xml` is unverified. SVG is
 *     an active document type, so a store that refuses it is behaving
 *     correctly, not failing.
 *
 * The proper endpoint is ask H in
 * DPA-FE `docs/Controlled/MainMenu/09-backend-request.md`.
 */
export const useUploadIcon = () =>
  useMutation((file) => {
    const formData = new FormData();
    // `file` is the field name Swagger declares. Not `Image`, not `File`.
    formData.append('file', file);
    return apiUpload('Degree/UploadFileToBlob', formData);
  });

/**
 * Find the URL in an upload response whose shape nobody documented.
 *
 * Returns `null` rather than a guess when nothing looks like a URL — the
 * caller then shows what the server actually sent, which is the only thing
 * that makes an undocumented endpoint debuggable from the browser.
 */
export const extractUploadedUrl = (data) => {
  if (typeof data === 'string') return data.trim() || null;
  if (!data || typeof data !== 'object') return null;

  const text = (value) =>
    typeof value === 'string' && value.trim() ? value.trim() : null;

  // The SPLIT shape first, because it is ambiguous with the direct one: the
  // media gallery on this API answers `{ url: <container>, imageURL: <name> }`,
  // where `url` alone is a folder and returning it would store a link to a
  // directory. Checking `url` first — as the obvious field name — silently
  // produced exactly that.
  const container = text(data.url);
  const fileName = [data.imageURL, data.imageUrl, data.fileName]
    .map(text)
    .find(Boolean);

  if (container && fileName && !/^https?:\/\//i.test(fileName)) {
    return `${container.replace(/\/+$/, '')}/${fileName.replace(/^\/+/, '')}`;
  }

  return (
    [
      data.url,
      data.fileUrl,
      data.imageUrl,
      data.imageURL,
      data.blobUrl,
      data.path,
      data.fileName,
    ]
      .map(text)
      .find(Boolean) ?? null
  );
};

/* -------------------------------------------------------------------------- */
/*  Shaping                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Nest a flat placement list by `parentPlacementId`, ordered by `sequenceId`.
 *
 * Defensive on two counts, because this renders an operator's live menu:
 *   - a row whose parent is missing is promoted to the root rather than
 *     vanishing, so a bad `parentPlacementId` is visible and fixable in the UI;
 *   - a parent cycle cannot hang the render — `seen` stops the descent.
 */
export const buildPlacementTree = (placements) => {
  const rows = Array.isArray(placements) ? placements : [];
  const byId = new Map(rows.map((row) => [row.id, { ...row, children: [] }]));
  const roots = [];

  byId.forEach((row) => {
    const parent = row.parentPlacementId ? byId.get(row.parentPlacementId) : null;
    if (parent && parent.id !== row.id) parent.children.push(row);
    else roots.push(row);
  });

  const bySequence = (a, b) =>
    (a.sequenceId ?? 0) - (b.sequenceId ?? 0) ||
    (a.item?.title ?? '').localeCompare(b.item?.title ?? '', 'ar');

  const sort = (nodes, seen = new Set()) =>
    nodes
      .filter((node) => !seen.has(node.id))
      .sort(bySequence)
      .map((node) => {
        seen.add(node.id);
        return { ...node, children: sort(node.children, seen) };
      });

  return sort(roots);
};

/**
 * Narrow a placement tree to the rows matching `term`, parents included.
 *
 * With 58 rows in the main menu, "find the Alumni entry" was a scroll-and-squint
 * job. Two rules make the result honest rather than merely shorter:
 *
 *   - a node whose OWN text matches keeps ALL its children, because hiding the
 *     children of a matched parent misrepresents that branch;
 *   - a node that does not match is kept only if some descendant does, carrying
 *     just the matching descendants — so a match is never orphaned from the
 *     parent that gives it context.
 *
 * Searches everything an operator can see on the row: title, description and
 * every shape the link can take.
 */
export const filterPlacementTree = (nodes, term) => {
  const needle = (term ?? '').trim().toLowerCase();
  const rows = Array.isArray(nodes) ? nodes : [];
  if (!needle) return rows;

  const matches = (node) => {
    const item = node.item ?? {};
    return [
      item.title,
      item.description,
      item.externalUrl,
      item.dynamicUrl,
      item.page?.slug,
      node.linkOverride,
      node.effectiveLink,
    ].some(
      (value) => typeof value === 'string' && value.toLowerCase().includes(needle)
    );
  };

  const walk = (list) =>
    list.reduce((kept, node) => {
      if (matches(node)) {
        kept.push(node);
        return kept;
      }
      const children = walk(node.children ?? []);
      if (children.length > 0) kept.push({ ...node, children });
      return kept;
    }, []);

  return walk(rows);
};

/** Ids of every node in the tree that HAS children — the collapsible ones. */
export const collectParentIds = (nodes) =>
  (Array.isArray(nodes) ? nodes : []).reduce((ids, node) => {
    if (node.children?.length > 0) {
      ids.push(node.id, ...collectParentIds(node.children));
    }
    return ids;
  }, []);

/* -------------------------------------------------------------------------- */
/*  Moving a row between parents                                               */
/* -------------------------------------------------------------------------- */

/**
 * How many levels a nesting menu may hold, counting the root as level 1.
 *
 * The website's own adapter walks three levels of real menu, and the editor
 * only ever offered «إضافة عنصر فرعي» on a root row, so three is what the data
 * and the UI already agree on. A drop that would push a branch past it is
 * refused rather than written — a menu deeper than the website renders is a
 * row the operator can see here and nowhere else.
 */
export const MAX_MENU_DEPTH = 3;

/** The node with this id, anywhere in the tree. */
export const findPlacementNode = (nodes, id) => {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    if (node.id === id) return node;
    const found = findPlacementNode(node.children, id);
    if (found) return found;
  }
  return null;
};

/** The node whose children include `id`, or `null` when `id` sits at the root. */
export const placementParentOf = (nodes, id) => {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    if ((node.children ?? []).some((child) => child.id === id)) return node;
    const found = placementParentOf(node.children, id);
    if (found) return found;
  }
  return null;
};

/** The sibling group a node belongs to — a parent's children, or the roots. */
const groupUnder = (tree, parent) => (parent ? parent.children ?? [] : tree);

/** 0 for a root row, 1 for its child, and so on. `-1` when not in the tree. */
export const placementDepthOf = (nodes, id, depth = 0) => {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    if (node.id === id) return depth;
    const found = placementDepthOf(node.children, id, depth + 1);
    if (found !== -1) return found;
  }
  return -1;
};

/** Levels in a branch, counting the node itself. A leaf is 1. */
export const placementHeightOf = (node) =>
  1 +
  (node?.children ?? []).reduce(
    (deepest, child) => Math.max(deepest, placementHeightOf(child)),
    0
  );

/** Is `id` inside `node`'s branch (or `node` itself)? Guards against a cycle. */
export const isPlacementDescendant = (node, id) =>
  node?.id === id ||
  (node?.children ?? []).some((child) => isPlacementDescendant(child, id));

/**
 * Work out what a drag-and-drop would actually write — or refuse it.
 *
 * Returns `null` for every move that must not happen, so one check at the call
 * site covers all of them: a row onto itself, a parent into its own branch
 * (which would detach that branch from the tree entirely), a drop that would
 * exceed `MAX_MENU_DEPTH`, and a move that lands exactly where the row already
 * is — a no-op deserves silence, not three API writes.
 *
 * Otherwise returns the two sibling groups IN THEIR FINAL ORDER. Resolving the
 * move to plain arrays here, away from React and away from the network, is what
 * makes it testable: the drop handler decides nothing.
 *
 *   position: 'inside' → last child of `targetId`
 *             'before' / 'after' → that side of `targetId`, in ITS group
 */
export const planPlacementMove = (tree, { movedId, targetId, position }) => {
  const rows = Array.isArray(tree) ? tree : [];
  if (!movedId || !targetId || movedId === targetId) return null;

  const moved = findPlacementNode(rows, movedId);
  const target = findPlacementNode(rows, targetId);
  if (!moved || !target) return null;

  // Dropping a branch inside itself would orphan everything below the cut.
  if (isPlacementDescendant(moved, targetId)) return null;

  const newParent =
    position === 'inside' ? target : placementParentOf(rows, targetId);
  const newParentId = newParent?.id ?? null;

  const newDepth = newParent ? placementDepthOf(rows, newParent.id) + 1 : 0;
  if (newDepth + placementHeightOf(moved) > MAX_MENU_DEPTH) return null;

  const oldParent = placementParentOf(rows, movedId);
  const oldParentId = oldParent?.id ?? null;
  const sameGroup = oldParentId === newParentId;

  // The destination group WITHOUT the moved row, so an index computed against
  // it is correct whether or not the row was already in it.
  const destinationBase = groupUnder(rows, newParent).filter(
    (node) => node.id !== movedId
  );

  let index;
  if (position === 'inside') {
    index = destinationBase.length;
  } else {
    const at = destinationBase.findIndex((node) => node.id === targetId);
    if (at === -1) return null;
    index = position === 'after' ? at + 1 : at;
  }

  const destination = [
    ...destinationBase.slice(0, index),
    moved,
    ...destinationBase.slice(index),
  ];

  // Already there: same parent, same slot.
  const currentIndex = groupUnder(rows, oldParent).findIndex(
    (node) => node.id === movedId
  );
  if (sameGroup && currentIndex === index) return null;

  return {
    moved,
    parentPlacementId: newParentId,
    destination,
    // Only a move BETWEEN groups leaves a gap behind to close.
    source: sameGroup
      ? null
      : groupUnder(rows, oldParent).filter((node) => node.id !== movedId),
  };
};

/**
 * Apply a plan from `planPlacementMove`.
 *
 * The moved row is written FIRST and with its new parent, because that write is
 * the move; the two renumbering passes only tidy `sequenceId` around it. If the
 * run fails part-way the row is already in its new parent and the orders are
 * stale — visibly wrong in one place, rather than a row that silently stayed
 * put. Sequential for the same reason `useReorderSiblings` is.
 *
 * `PUT /Placements/{id}` is a FULL REPLACE, so every write carries the row's
 * current values, not just the fields being changed.
 */
export const useMovePlacement = () => {
  const queryClient = useQueryClient();

  const write = (row, { parentPlacementId, sequenceId }) =>
    apiPut(`${RESOURCE}/Placements/${row.id}`, {
      navigationItemId: row.navigationItemId,
      placementType: row.placementType,
      parentPlacementId: parentPlacementId ?? null,
      linkOverride: row.linkOverride ?? null,
      isVisible: row.isVisible,
      sequenceId,
    });

  return useMutation(
    async ({ moved, parentPlacementId, destination, source }) => {
      const index = destination.findIndex((row) => row.id === moved.id);
      await write(moved, { parentPlacementId, sequenceId: index });

      const renumber = async (group) => {
        for (let i = 0; i < group.length; i += 1) {
          const row = group[i];
          if (row.id === moved.id) continue;
          if (row.sequenceId === i) continue;
          // eslint-disable-next-line no-await-in-loop
          await write(row, {
            parentPlacementId: row.parentPlacementId ?? null,
            sequenceId: i,
          });
        }
      };

      await renumber(destination);
      if (source) await renumber(source);
      return true;
    },
    { onSuccess: () => queryClient.invalidateQueries(navigationKeys.all) }
  );
};

/** Count every node in a placement tree, children included. */
export const countPlacements = (nodes) =>
  (Array.isArray(nodes) ? nodes : []).reduce(
    (total, node) => total + 1 + countPlacements(node.children),
    0
  );
